import { useEffect, useMemo, useRef } from "react"
import { ShoppingCart, SunHorizon } from "@phosphor-icons/react"

import { Button } from "~/components/reusable/Button"
import Commandbar from "~/components/planning/Commandbar"
import { SnoozeDialog } from "~/components/purchaseOrder/tabs/SnoozeDialog"
import { useVariantsByIds } from "~/hooks/useVariants"
import { trackEvent } from "~/hooks/useTrackingService"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { api } from "~/utils/api"
import { CreateBuyDialog } from "./CreateBuyDialog"
import { NoosVariantOverflow } from "./NoosVariantOverflow"
import { useTriggersWarehouseId } from "./useNoosTriggers"

type DelayedPoWarning = {
  poName: string
  poExpectedArrivalDate: Date
  allPos: {
    poName: string
    warehouseId: string
    warehouseName: string
    poExpectedArrivalDate: Date
  }[]
}

type Props = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedVariants: () => void
  delayedPoWarningMap?: Map<string, DelayedPoWarning>
  snoozedUntilMap?: Map<string, Date>
}

export function NoosCommandBar({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedVariants,
  delayedPoWarningMap: delayedPoWarningMapProp,
  snoozedUntilMap,
}: Props) {
  const { nextBuyDate, supplyPlan } = useNoosSupplyPlanDetailContext()
  const [warehouseId] = useTriggersWarehouseId()

  // Clear selection when next buy date changes — variants with suggestions can change entirely,
  // and hidden rows can't be deselected. clearSelectedVariants() properly purges persistent state.
  // Use ref comparison instead of isFirstMount to survive React StrictMode's double-invocation.
  const prevNextBuyDateRef = useRef(nextBuyDate)
  useEffect(() => {
    if (prevNextBuyDateRef.current === nextBuyDate) return
    prevNextBuyDateRef.current = nextBuyDate
    clearSelectedVariants()
  }, [nextBuyDate, clearSelectedVariants])
  const variants = useVariantsByIds(selectedVariantIds)

  const utils = api.useUtils()

  const { data: delayedPoWarnings } = api.stockOutTimes.getDelayedPoSuggestionWarnings.useQuery(
    { variantIds: selectedVariantIds },
    { enabled: selectedVariantIds.length > 0 && !delayedPoWarningMapProp, staleTime: 0 },
  )
  const delayedPoWarningMap = useMemo(
    () =>
      delayedPoWarningMapProp ?? new Map((delayedPoWarnings ?? []).map((w) => [w.variantId, w])),
    [delayedPoWarningMapProp, delayedPoWarnings],
  )

  const { data: snoozedVariants } = api.variantMetadata.getSnoozedVariants.useQuery(
    { variantIds: selectedVariantIds },
    { enabled: selectedVariantIds.length > 0 && !snoozedUntilMap, staleTime: 0 },
  )
  const snoozedSelectedIds = useMemo(
    () =>
      selectedVariantIds.filter((id) =>
        snoozedUntilMap
          ? snoozedUntilMap.has(id)
          : snoozedVariants?.some((s) => s.variantId === id),
      ),
    [selectedVariantIds, snoozedUntilMap, snoozedVariants],
  )

  const { mutate: unsnoozeVariants, isPending: isUnsnoozePending } =
    api.variantMetadata.unsnoozeVariants.useMutation({
      onSuccess: (_data, variables) => {
        trackEvent("Variant Unsnoozed", {
          "Number Of Variants Unsnoozed": variables.variantIds.length,
        })
        utils.variantMetadata.getSnoozedVariants.invalidate()
        clearSelectedVariants()
      },
    })

  if (selectedVariantCount === 0) return null

  return (
    <Commandbar count={selectedVariantCount} onClear={clearSelectedVariants}>
      <CreateBuyDialog
        supplyPlanId={supplyPlan.id}
        scopedVariantIds={selectedVariantIds}
        defaultWarehouseId={warehouseId}
        onCreated={clearSelectedVariants}
        trigger={
          <Button variant="primary" size="sm">
            <ShoppingCart />
            Create buy
          </Button>
        }
      />
      <SnoozeDialog
        variants={variants}
        delayedPoWarningMap={delayedPoWarningMap}
        onSuccess={clearSelectedVariants}
      />
      {snoozedSelectedIds.length > 0 && (
        <Button
          variant="neutral"
          size="sm"
          requires="edit"
          loading={isUnsnoozePending}
          onClick={() => unsnoozeVariants({ variantIds: snoozedSelectedIds })}
        >
          <SunHorizon />
          Unsnooze
        </Button>
      )}
      <NoosVariantOverflow
        selectedVariantIds={selectedVariantIds}
        variants={variants}
        onApplied={clearSelectedVariants}
      />
    </Commandbar>
  )
}
